/**
 * Consent Management Types
 *
 * Shared type definitions for consent management across the application.
 */

// =============================================================================
// Consent State
// =============================================================================

/**
 * Simplified consent state enum.
 * Used for quick consent checks on the client side.
 */
export enum ConsentState {
  /** User has explicitly consented to all cookies/tracking */
  CONSENTED = "1",
  /** User has explicitly NOT consented (rejected or partially rejected) */
  NOT_CONSENTED = "0",
  /** No consent decision has been made yet */
  UNKNOWN = "unknown",
}

// =============================================================================
// OneTrust Consent Groups
// =============================================================================

/**
 * Consent groups parsed from the OneTrust cookie.
 * Keys are category IDs (e.g., "C0001", "C0002"), values are "0" or "1".
 *
 * Common OneTrust categories:
 * - C0001: Strictly Necessary (always "1")
 * - C0002: Performance
 * - C0003: Functional
 * - C0004: Targeting/Advertising
 */
export type ConsentGroups = Record<string, string>;

/**
 * Result of checking the full consent status from OneTrust.
 */
export interface ConsentCheckResult {
  /** True if the OneTrust cookie has been set and is parseable */
  isReady: boolean;
  /** True only if user consented to ALL categories */
  hasConsentedAll: boolean;
  /** Parsed consent groups (category ID -> consent value) */
  groups: ConsentGroups;
  /** Number of times user has interacted with the consent UI */
  interactionCount?: number;
  /** True if Global Privacy Control (GPC) signal is active */
  gpcActive?: boolean;
}

// =============================================================================
// Consent Configuration
// =============================================================================

/**
 * Configuration for consent-aware initialization.
 */
export interface ConsentAwareOptions {
  /**
   * Whether to require explicit consent before running (opt-in regions like GDPR).
   * When true, unknown consent state blocks execution.
   * When false, unknown consent state allows execution (opt-out model like CCPA).
   * @default false
   */
  requireExplicitConsent?: boolean;
  /** Enable debug console logging */
  debug?: boolean;
}

/**
 * Consent model names from OneTrust.
 */
export type ConsentModelName =
  | "opt-in"
  | "opt-out"
  | "notice-only"
  | "implied"
  | string;

// =============================================================================
// Tracking Module Consent
// =============================================================================

/**
 * Consent mode for tracking modules.
 */
export type ConsentMode = "full" | "limited";

/**
 * Options passed to tracking module initialization.
 */
export interface ModuleConsentOptions {
  /** Whether personalized tracking/ads are allowed */
  personalized: boolean;
  /** Current consent mode */
  consentMode: ConsentMode;
}

// =============================================================================
// Cookie Building Options
// =============================================================================

/**
 * Options for building cookie strings.
 */
export interface CookieOptions {
  /** Cookie path (default: "/") */
  path?: string;
  /** Cookie domain (e.g., ".example.com") */
  domain?: string;
  /** Whether to use secure flag and SameSite=None (default: true) */
  secure?: boolean;
}
